import getConfig from './config';
import { dataServiceFetch, indexerFetch } from './indexerFallback';
import { isStablePool } from './near';
import _ from 'lodash';
import { parsePoolView, PoolRPCView } from './api';
import moment from 'moment/moment';
import { volumeType, TVLType } from 'src/state/pool';
import db from '../store/RefDatabase';
import { getCurrentWallet } from '../utils/wallets-integration';
import { filterBlackListPools } from './near';

import { BLACKLIST_POOL_IDS } from './near';
import { TokenMetadata } from './ft-contract';
import { getAuthenticationHeaders } from './signature';

const config = getConfig();

const genUrlParams = (props: Record<string, string | number>) => {
  return Object.keys(props)
    .map((key) => key + '=' + props[key])
    .join('&');
};

export const getPoolsByTokensIndexer = async ({
  token0,
  token1,
}: {
  token0: string;
  token1: string;
}) => {
  const pools = await indexerFetch(
    `/list-pools-by-tokens?token0=${token0}&token1=${token1}`,
    {
      method: 'GET',
      headers: getAuthenticationHeaders('/list-pools-by-tokens'),
    }
  ).then((response) => response.json());

  if (pools?.code === -1 && pools?.data === null) return [];

  return pools.filter(
    (pool: any) =>
      !isStablePool(pool.id) && !BLACKLIST_POOL_IDS.includes(pool.id)
  );
};

export const getPoolMonthVolume = async (
  pool_id: string
): Promise<volumeType[]> => {
  return await dataServiceFetch(`/pool/${pool_id}/volume`, {
    method: 'GET',
  })
    .then((response) => response.json())
    .then((monthVolume) => {
      return monthVolume.slice(0, 60);
    });
};

export const getPoolMonthTVL = async (pool_id: string): Promise<TVLType[]> => {
  return await dataServiceFetch(`/pool/${pool_id}/tvl`, {
    method: 'GET',
  })
    .then((response) => response.json())
    .then((monthTVL) => {
      return monthTVL.slice(0, 60);
    });
};

export interface OrderTxType {
  order_id: string;
  tx_id: string | null;
  receipt_id: string | null;
}

export const getHistoryOrder = async (
  account_id: string
): Promise<OrderTxType[]> => {
  return await indexerFetch(`/get-limit-order-log-by-account/${account_id}`, {
    method: 'GET',
    headers: getAuthenticationHeaders(
      `/get-limit-order-log-by-account/${account_id}`
    ),
  }).then((response) => response.json());
};
export interface HistoryOrderSwapInfo {
  tx_id: string;
  token_in: string;
  token_out: string;
  pool_id: string;
  point: string;
  amount_in: string;
  amount_out: string;
  timestamp: string;
  receipt_id: string;
}

export const getHistoryOrderSwapInfo = async (
  account_id: string
): Promise<HistoryOrderSwapInfo[]> => {
  return await indexerFetch(`/get-limit-order-swap-by-account/${account_id}`, {
    method: 'GET',
    headers: getAuthenticationHeaders(
      `/get-limit-order-swap-by-account/${account_id}`
    ),
  }).then((response) => response.json());
};

export const get24hVolume = async (pool_id: string): Promise<string> => {
  return await dataServiceFetch(`/poollist/${pool_id}/24hvolume/sum`, {
    method: 'GET',
    headers: getAuthenticationHeaders(`/api/poollist/${pool_id}/24hvolume/sum`),
  })
    .then((response) => response.json())
    .then((monthTVL) => {
      return monthTVL.toString();
    })
    .catch(() => {
      return undefined;
    });
};

export const get24hVolumes = async (
  pool_ids: (string | number)[]
): Promise<string[]> => {
  const batchSize = 300;
  const numBatches = Math.ceil(pool_ids.length / batchSize);
  const promises: Promise<string[]>[] = [];

  for (let batchIndex = 0; batchIndex < numBatches; batchIndex++) {
    const batchIds = pool_ids.slice(
      batchIndex * batchSize,
      (batchIndex + 1) * batchSize
    );
    const promise = dataServiceFetch(
      `/poollist/${batchIds.join('|')}/24hvolume/sum`,
      {
        method: 'GET',
        headers: getAuthenticationHeaders(
          `/api/poollist/${batchIds.join('|')}/24hvolume/sum`
        ),
      }
    )
      .then((response) => response.json())
      .then((batchData) => batchData.map((volume: any) => volume.toString()));

    promises.push(promise);
  }

  const results = await Promise.all(promises);
  return results.flat();
};

export const getYourPools = async (): Promise<PoolRPCView[]> => {
  const account_id = getCurrentWallet()?.wallet?.getAccountId();
  return await indexerFetch('/liquidity-pools/' + account_id, {
    method: 'GET',
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
      ...getAuthenticationHeaders(`/liquidity-pools/${account_id}`),
    },
  })
    .then((response) => response.json())
    .then((pools) => {
      return pools;
    });
};

export const getTopPoolsIndexerRaw = async () => {
  const timeoutDuration = 20000; // 20 seconds in milliseconds

  const controller = new AbortController();
  const timeout = setTimeout(() => {
    controller.abort();
  }, timeoutDuration);

  try {
    const response = await indexerFetch('/list-top-pools', {
      method: 'GET',
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
        ...getAuthenticationHeaders('/list-top-pools'),
      },
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    return await response.json();
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timed out');
    } else {
      throw error;
    }
  }
};

export const getTopPools = async (): Promise<PoolRPCView[]> => {
  try {
    let pools: any;

    if (await db.checkTopPools()) {
      pools = await db.queryTopPools();
    } else {
      pools = await indexerFetch('/list-top-pools', {
        method: 'GET',
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          ...getAuthenticationHeaders('/list-top-pools'),
        },
      }).then((response) => response.json());

      // include non-stable pools on top pool list
      // TODO:

      // await Promise.all(
      //   ALL_STABLE_POOL_IDS.concat(BLACKLIST_POOL_IDS)
      //     .filter((id) => Number(id) !== Number(STABLE_POOL_ID))
      //     .filter((_) => _)
      //     .map(async (id) => {
      //       const pool = await getPoolRPC(Number(id));

      //       const ids = pool.tokenIds;

      //       const twoTokenStablePoolIds = (
      //         await getPoolsByTokensIndexer({
      //           token0: ids[0],
      //           token1: ids[1],
      //         })
      //       ).map((p: any) => p.id.toString());

      //       const twoTokenStablePools = await getPoolsByIds({
      //         pool_ids: twoTokenStablePoolIds,
      //       });

      //       if (twoTokenStablePools.length > 0) {
      //         const maxTVLPool = _.maxBy(twoTokenStablePools, (p) => p.tvl);

      //         if (
      //           pools.find(
      //             (pool: any) => Number(pool.id) === Number(maxTVLPool.id)
      //           )
      //         )
      //           return;

      //         pools.push(_.maxBy(twoTokenStablePools, (p) => p.tvl));
      //       }
      //     })
      // );

      await db.cacheTopPools(pools);
    }

    pools = pools.map((pool: any) => parsePoolView(pool));

    return pools
      .filter((pool: { token_account_ids: string | any[]; id: any }) => {
        return !isStablePool(pool.id) && pool.token_account_ids.length < 3;
      })
      .filter(filterBlackListPools);
  } catch (error) {
    return [];
  }
};

export const getSearchResult = async ({
  type = 'classic',
  sort = 'tvl',
  limit = '100',
  offset = '0',
  farm = 'false',
  hide_low_pool = 'false',
  order = 'desc',
  token_type = '',
  token_list = '',
  pool_id_list = '',
  onlyUseId = false,
}: {
  type?: string;
  sort?: string;
  limit?: string;
  offset?: string;
  farm?: string | boolean;
  hide_low_pool?: string | boolean;
  order?: string;
  token_list?: string;
  token_type?: string;
  pool_id_list?: string;
  onlyUseId?: boolean;
}): Promise<PoolRPCView[]> => {
  let tktype = token_type;
  if (token_type == 'all') {
    tktype = '';
  } else if (token_type == 'stablecoin') {
    tktype = 'stable_coin';
  }
  if (sort == 'apr' || sort == 'apy') {
    sort = 'apy';
  }
  if (sort == 'volume_24h') {
    sort = '24h';
  }
  try {
    let pools: any;
    const url = !onlyUseId
      ? `/pool/search?type=${type}&sort=${sort}&limit=${limit}&offset=${offset}&farm=${farm}&hide_low_pool=${hide_low_pool}&order_by=${order}&token_type=${tktype}&token_list=${token_list}&pool_id_list=${pool_id_list}`
      : `/pool/search?pool_id_list=${pool_id_list}`;
    pools = await indexerFetch(url, {
      method: 'GET',
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
        ...getAuthenticationHeaders('/pool/search'),
      },
    }).then((response) => response.json());
    localStorage.setItem('poolsTotal', pools.data.total);

    if (pools?.data?.list.length > 0) {
      pools = pools.data.list;
      return pools
        .filter((pool: { token_account_ids: string | any[]; id: any }) => {
          return !isStablePool(pool.id) && pool.token_account_ids.length < 3;
        })
        .filter(filterBlackListPools);
    } else {
      pools = [];
      return [];
    }
  } catch (error) {
    console.log(error, 'usePools error');
    return [];
  }
};

export const getPool = async (pool_id: string): Promise<PoolRPCView> => {
  return await indexerFetch('/get-pool?pool_id=' + pool_id, {
    method: 'GET',
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
      ...getAuthenticationHeaders('/get-pool'),
    },
  })
    .then((response) => response.json())
    .then((pool) => {
      return parsePoolView(pool);
    });
};
const parsePoolTxTimeStamp = (nanosecondTimestamp: string) => {
  return moment(Math.floor(Number(nanosecondTimestamp) / 1000000)).format(
    'YYYY-MM-DD HH:mm:ss'
  );
};

export interface ClassicPoolSwapTransaction {
  token_in: string;
  token_out: string;
  swap_in: string;
  swap_out: string;
  timestamp: string;
  tx_id: string;
  receipt_id: string;
}

export const getClassicPoolSwapRecentTransaction = async (props: {
  pool_id: string | number;
}) => {
  const paramString = genUrlParams(props);

  return await indexerFetch(`/get-recent-transaction-swap?${paramString}`, {
    method: 'GET',
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
      ...getAuthenticationHeaders('/get-recent-transaction-swap'),
    },
  })
    .then((response) => response.json())
    .then((transactions: ClassicPoolSwapTransaction[]) => {
      return (transactions || []).map((transaction) => {
        return {
          ...transaction,
          timestamp: /^\d+$/.test(transaction.timestamp || '')
            ? parsePoolTxTimeStamp(transaction.timestamp)
            : transaction.timestamp || '—',
        };
      });
    });
};

export interface DCLPoolSwapTransaction {
  token_in: string;
  token_out: string;
  amount_in: string;
  amount_out: string;
  timestamp: string;
  tx_id: string;
  receipt_id: string;
}

export const getDCLPoolSwapRecentTransaction = async (props: {
  pool_id: string | number;
}) => {
  const paramString = genUrlParams(props);

  return await indexerFetch(`/get-recent-transaction-dcl-swap?${paramString}`, {
    method: 'GET',
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
      ...getAuthenticationHeaders('/get-recent-transaction-dcl-swap'),
    },
  })
    .then((response) => response.json())
    .then((transactions: DCLPoolSwapTransaction[]) => {
      return transactions.map((transaction) => ({
        ...transaction,
        timestamp: parsePoolTxTimeStamp(transaction.timestamp),
      }));
    });
};

export interface ClassicPoolLiquidityRecentTransaction {
  method_name: string;
  timestamp: string;
  token_in: string;
  token_out: string;
  amount_in: string;
  amount_out: string;
  tx_id: string;
  shares?: string;
  pool_id?: string;
  amounts?: string;
  receipt_id: string;
}

export const getClassicPoolLiquidityRecentTransaction = async (props: {
  pool_id: string | number;
}) => {
  const paramString = genUrlParams(props);

  return await indexerFetch(
    `/get-recent-transaction-liquidity?${paramString}`,
    {
      method: 'GET',
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
        ...getAuthenticationHeaders('/get-recent-transaction-liquidity'),
      },
    }
  )
    .then((response) => response.json())
    .then((transactions: ClassicPoolLiquidityRecentTransaction[]) => {
      return (transactions || []).map((transaction) => ({
        ...transaction,
        timestamp: /^\d+$/.test(transaction.timestamp || '')
          ? parsePoolTxTimeStamp(transaction.timestamp)
          : transaction.timestamp || '—',
      }));
    });
};

export interface DCLPoolLiquidityRecentTransaction {
  method_name: string;
  amount_x: string;
  amount_y: string;
  timestamp: string;
  tx_id: string;
  receipt_id: string;
}

export const getDCLPoolLiquidityRecentTransaction = async (props: {
  pool_id: string | number;
}) => {
  const paramString = genUrlParams(props);

  return await indexerFetch(
    `/get-recent-transaction-dcl-liquidity?${paramString}`,
    {
      method: 'GET',
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
        ...getAuthenticationHeaders('/get-recent-transaction-dcl-liquidity'),
      },
    }
  )
    .then((response) => response.json())
    .then((transactions: DCLPoolLiquidityRecentTransaction[]) => {
      return transactions.map((transaction) => ({
        ...transaction,
        timestamp: parsePoolTxTimeStamp(transaction.timestamp),
      }));
    });
};

export interface LimitOrderRecentTransaction {
  method_name: string;
  timestamp: string;
  amount: string;
  tx_id: string;
  point: string;
  sell_token: string;
  receipt_id: string;
}

export const getLimitOrderRecentTransaction = async (props: {
  pool_id: string | number;
}) => {
  const paramString = genUrlParams(props);

  return await indexerFetch(
    `/get-recent-transaction-limit-order?${paramString}`,
    {
      method: 'GET',
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
        ...getAuthenticationHeaders('/get-recent-transaction-limit-order'),
      },
    }
  )
    .then((response) => response.json())
    .then((transactions: LimitOrderRecentTransaction[]) => {
      return transactions.map((transaction) => ({
        ...transaction,
        timestamp: parsePoolTxTimeStamp(transaction.timestamp),
      }));
    });
};

interface DCLPoolFee {
  total_fee: string;
  total_liquidity: string;
}

export const getDCLTopBinFee = async (props: {
  pool_id: string;
  bin: number;
  start_point: number;
  end_point: number;
}): Promise<DCLPoolFee> => {
  const { pool_id, bin, start_point, end_point } = props;
  const result = await getDclPoolPoints(pool_id, bin, start_point, end_point);
  return result?.top_bin_fee_data;
};

export const getDCLAccountFee = async (props: {
  pool_id: string | number;
  account_id: string | number;
}): Promise<DCLPoolFee> => {
  const paramString = genUrlParams(props);
  try {
    return await indexerFetch(`/get-fee-by-account?${paramString}`, {
      method: 'GET',
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
        ...getAuthenticationHeaders('/get-fee-by-account'),
      },
    }).then((response) => response.json());
  } catch (error) {
    return;
  }
};

export const getPoolsByIds = async ({
  pool_ids,
}: {
  pool_ids: string[];
}): Promise<PoolRPCView[]> => {
  const ids = pool_ids.join('|');
  if (!ids) return [];
  return indexerFetch('/list-pools-by-ids?ids=' + ids, {
    method: 'GET',
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
      ...getAuthenticationHeaders('/list-pools-by-ids'),
    },
  })
    .then((response) => response.json())
    .then((pools) => {
      pools = pools.map((pool: any) => parsePoolView(pool));
      return pools;
    })
    .catch(() => {
      return [];
    });
};

export const getPoolsDetailById = async ({ pool_id }: { pool_id: string }) => {
  return indexerFetch('/pool/detail?pool_id=' + pool_id, {
    method: 'GET',
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
      ...getAuthenticationHeaders('/pool/detail'),
    },
  })
    .then((response) => response.json())
    .then((pools) => {
      console.log(pools);
      return pools.data;
    })
    .catch(() => {
      return [];
    });
};

export const getTokenPriceList = async (): Promise<any> => {
  return await indexerFetch('/list-token-price', {
    method: 'GET',
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
      ...getAuthenticationHeaders('/list-token-price'),
    },
  })
    .then((response) => response.json())
    .then((responseBody) => {
      return responseBody;
    });
};

export const getIndexerStatus = async (): Promise<any> => {
  return await indexerFetch('/get-service-version', {
    method: 'GET',
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
      ...getAuthenticationHeaders('/get-service-version'),
    },
  }).then((response) => response.status !== 502);
};

export const _search = (args: any, pools: PoolRPCView[]) => {
  if (args.tokenName === '') return pools;
  return _.filter(pools, (pool: PoolRPCView) => {
    return (
      _.includes(
        pool.token_symbols[0]?.toLowerCase(),
        args.tokenName?.toLowerCase()
      ) ||
      _.includes(
        pool.token_symbols[1]?.toLowerCase(),
        args.tokenName?.toLowerCase()
      )
    );
  });
};

export const _order = (args: any, pools: PoolRPCView[]) => {
  let column = args.column || 'tvl';
  const order = args.order || 'desc';
  column = args.column === 'fee' ? 'total_fee' : column;
  return _.orderBy(pools, [column], [order]);
};

export const getV3PoolVolumeById = async (pool_id: string): Promise<any[]> => {
  return await indexerFetch('/get-dcl-pools-volume?pool_id=' + pool_id, {
    method: 'GET',
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
      ...getAuthenticationHeaders('/get-dcl-pools-volume'),
    },
  })
    .then((response) => response.json())
    .then((entries) => {
      (entries || []).sort((entryA, entryB) => {
        const timeDifference =
          new Date(entryA.dateString).getTime() -
          new Date(entryB.dateString).getTime();
        return timeDifference;
      });
      return entries.slice(-60);
    })
    .catch(() => {
      return [];
    });
};
export const getV3poolTvlById = async (pool_id: string): Promise<any[]> => {
  return await indexerFetch('/get-dcl-pools-tvl-list?pool_id=' + pool_id, {
    method: 'GET',
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
      ...getAuthenticationHeaders('/get-dcl-pools-tvl-list'),
    },
  })
    .then((response) => response.json())
    .then((responseBody) => {
      return responseBody.slice(0, 60);
    })
    .catch(() => {
      return [];
    });
};

export const getV3Pool24VolumeById = async (pool_id: string): Promise<any> => {
  return await indexerFetch('/get-24h-volume-by-id?pool_id=' + pool_id, {
    method: 'GET',
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
      ...getAuthenticationHeaders('/get-24h-volume-by-id'),
    },
  })
    .then((response) => response.json())
    .then((value) => {
      return value;
    })
    .catch(() => {
      return 0;
    });
};
export const getAllV3Pool24Volume = async (): Promise<any[]> => {
  return await indexerFetch('/get-24h-volume-list', {
    method: 'GET',
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
      ...getAuthenticationHeaders('/get-24h-volume-list'),
    },
  })
    .then((response) => response.json())
    .then((responseBody) => {
      return responseBody;
    })
    .catch(() => {
      return [];
    });
};

export const getAllPoolData = async () => {
  return await indexerFetch('/all-pool-data', {
    method: 'GET',
    headers: {
      ...getAuthenticationHeaders('/all-pool-data'),
    },
  })
    .then((response) => response.json())
    .then((responseBody) => {
      return responseBody.data;
    });
};

export interface TokenPairRate {
  symbol: string;
  contract_address: string;
  price_list: PriceList[];
}

interface PriceList {
  price: number;
  date_time: number;
}

export const getTokenPairRate = async ({
  token,
  base_token,
  dimension,
}: {
  token: TokenMetadata;
  base_token: TokenMetadata;
  dimension: 'Y' | 'M' | 'W' | 'D' | 'All';
}): Promise<TokenPairRate> => {
  return await indexerFetch(
    `/token-price-report?token=${token.id}&base_token=${base_token.id}&dimension=${dimension}`,
    {
      method: 'GET',
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
        ...getAuthenticationHeaders('/token-price-report'),
      },
    }
  )
    .then(async (response) => {
      const priceHistory = await response.json();
      return {
        ...priceHistory,
        price_list: priceHistory.price_list.map((pricePoint: any) => ({
          price: Number(pricePoint.price),
          date_time: pricePoint.date_time * 1000,
        })),
      };
    })

    .catch(() => {
      return {
        symbol: token.symbol,
        contract_address: token.id,
        price_list: [],
      };
    });
};

export const getDclPoolPoints = async (
  pool_id: string,
  bin: number,
  start_point: number,
  end_point: number
) => {
  return await indexerFetch(
    `/get-dcl-points?pool_id=${pool_id}&slot_number=${bin}&start_point=${start_point}&end_point=${end_point}`,
    {
      headers: getAuthenticationHeaders('/get-dcl-points'),
    }
  )
    .then(async (response) => {
      const responseBody = await response.json();
      return responseBody;
    })
    .catch(() => {
      return {};
    });
};

export const getTxId = async (receipt_id: string) => {
  return await fetch(config.txIdApiUrl + `/v1/search/?keyword=${receipt_id}`)
    .then(async (response) => {
      const responseBody = await response.json();
      return responseBody;
    })
    .catch(() => {
      return [];
    });
};
export const getTokens = async () => {
  return await indexerFetch('/list-token', {
    method: 'GET',
    headers: { 'Content-type': 'application/json; charset=UTF-8' },
  }).then(async (response) => {
    const tokens = await response.json();
    return tokens;
  });
};
export const addUserWallet = async (params) => {
  return await indexerFetch('/add-user-wallet', {
    method: 'POST',
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
      ...getAuthenticationHeaders('/add-user-wallet'),
    },
    body: JSON.stringify(params),
  }).catch(async (error) => {
    console.log('add user wallet failed', error);
  });
};
